import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../../../constants";
import { Check, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, Chip, CircularProgress, Grid,
  TextField, InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "../../../../language";

const EVALUATION_ORDER = { H: 0, L: 1, N: 2 };

const RANK_PREFIX_MAP = { d: "Domain", p: "Phylum", c: "Class", o: "Order", f: "Family", g: "Genus", s: "Species" };
const RANK_ORDER = ["Domain", "Phylum", "Class", "Order", "Family", "Genus", "Species"];

function evalColor(evaluation, theme) {
  if (evaluation === "H" || evaluation === "L") return theme.palette.traffic.orange;
  return theme.palette.traffic.green;
}

function evalLabel(evaluation, t) {
  const key = evaluation === "H" ? "bt_status_high" : evaluation === "L" ? "bt_status_low" : null;
  if (!key) return "";
  return t(key) !== key ? t(key) : evaluation;
}

function getTaxonomyRank(taxonomy) {
  if (typeof taxonomy !== "string" || !taxonomy) return null;
  const segments = taxonomy.split(";");
  const prefix = segments[segments.length - 1].split("__")[0];
  return RANK_PREFIX_MAP[prefix] || null;
}

export default function GutBacteriaAll({ date }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]     = useState("");
  const [sortField, setSortField] = useState("status");
  const [sortDir, setSortDir]     = useState("asc");
  const [levelFilter, setLevelFilter] = useState("All");

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/gut/bacteria-records/?date=${date}`)
      .then(res => setRecords(res.data))
      .catch(err => console.error("Fetch error:", err))
      .finally(() => setLoading(false));
  }, [date]);

  const allGenus = records.filter(r => r.category === "all_genus");

  if (loading) {
    return <Box textAlign="center" my={4}><CircularProgress size={28} /></Box>;
  }

  if (!allGenus.length) return null;

  const availableRanks = RANK_ORDER.filter(rank => allGenus.some(item => getTaxonomyRank(item.taxonomy) === rank));

  const outOfRangeCount = allGenus.filter(i => i.evaluation === "H" || i.evaluation === "L").length;
  const counts = allGenus.reduce(
    (acc, i) => {
      if (i.evaluation === "H") acc.high++;
      else if (i.evaluation === "L") acc.low++;
      else acc.normal++;
      return acc;
    },
    { high: 0, low: 0, normal: 0 },
  );

  const dark = theme.palette.mode === "dark";
  // Same glass-card treatment as the other marker/category cards — rounded,
  // blurred, translucent, no flat fill.
  const glassSx = {
    borderRadius: "16px",
    overflow: "hidden",
    bgcolor: dark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.35)"}`,
    boxShadow: dark
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  };

  return (
    <Grid container>
      <Grid size={12} sx={{ mb: 2 }}>
        <Box sx={glassSx}>
          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            square
            sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}
          >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: 2.5, minHeight: 44,
              "& .MuiAccordionSummary-content": { my: 0.75, display: "flex", alignItems: "center", gap: 1, pr: 2 },
              "& .MuiAccordionSummary-expandIconWrapper": { ml: 1.25 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: 0.2 }}>
                Bacteria List
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            {outOfRangeCount > 0 ? (
              <Chip
                label={`${outOfRangeCount}`}
                size="small"
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.traffic.orange,
                  border: "1px solid", borderColor: theme.palette.traffic.orange,
                  fontWeight: 500, fontSize: "0.8rem",
                  width: 22, height: 22,
                  borderRadius: "999px",
                  "& .MuiChip-label": { px: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" },
                }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Check size={18} strokeWidth={2.5} color={theme.palette.traffic.green} />
              </Box>
            )}
          </AccordionSummary>

          <AccordionDetails sx={{ p: 0 }}>
            {/* Stats row */}
            <Box sx={{
              display: "flex", gap: 1.5, px: 2.5, py: 1,
              bgcolor: theme.palette.mode === "light" ? "grey.C100" : "greydark.C900",
              borderBottom: "1px solid", borderColor: "divider",
            }}>
              {[
                { label: "High",   count: counts.high,   color: theme.palette.traffic.orange },
                { label: "Low",    count: counts.low,    color: theme.palette.traffic.orange },
                { label: "Normal", count: counts.normal, color: theme.palette.traffic.green  },
              ].map(({ label, count, color }) => (
                <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Chip
                    label={count}
                    size="small"
                    sx={{ bgcolor: color, color: "#fff", fontWeight: 700, height: 20, minWidth: 28, "& .MuiChip-label": { px: 0.75 } }}
                  />
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                </Box>
              ))}
            </Box>

            {/* Taxonomic level filter */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider", flexWrap: "wrap" }}>
              {["All", ...availableRanks].map((rank) => {
                const active = levelFilter === rank;
                return (
                  <Box
                    key={rank}
                    onClick={() => setLevelFilter(rank)}
                    sx={{
                      px: 1, py: 0.5, borderRadius: 1.5, cursor: "pointer",
                      border: "1px solid",
                      borderColor: active ? "primary.main" : "divider",
                      bgcolor: active
                        ? (theme.palette.mode === "light" ? "grey.C100" : "greydark.C800")
                        : "transparent",
                      userSelect: "none",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <Typography variant="caption" fontWeight={active ? 700 : 400} color={active ? "primary.main" : "text.secondary"}>
                      {rank}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Search + Sort */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
              <TextField
                size="small"
                sx={{ flex: 1 }}
                placeholder="Search bacteria…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={16} color={theme.palette.text.disabled} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {[
                { field: "value",  label: "Value"  },
                { field: "status", label: "Status" },
              ].map(({ field, label }) => {
                const active = sortField === field;
                const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                return (
                  <Box
                    key={field}
                    onClick={() => handleSort(field)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      px: 1, py: 0.5, borderRadius: 1.5, cursor: "pointer",
                      border: "1px solid",
                      borderColor: active ? "primary.main" : "divider",
                      bgcolor: active
                        ? (theme.palette.mode === "light" ? "grey.C100" : "greydark.C800")
                        : "transparent",
                      userSelect: "none",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <Typography variant="caption" fontWeight={active ? 700 : 400} color={active ? "primary.main" : "text.secondary"}>
                      {label}
                    </Typography>
                    <Icon size={12} color={active ? theme.palette.primary.main : theme.palette.text.disabled} />
                  </Box>
                );
              })}
            </Box>

            {/* Rows */}
            {allGenus
              .filter(item => !search || item.genus?.toLowerCase().includes(search.toLowerCase()))
              .filter(item => levelFilter === "All" || getTaxonomyRank(item.taxonomy) === levelFilter)
              .sort((a, b) => {
                const dir = sortDir === "asc" ? 1 : -1;
                if (sortField === "value") return (Number(a.frequency) - Number(b.frequency)) * dir;
                return ((EVALUATION_ORDER[a.evaluation] ?? 3) - (EVALUATION_ORDER[b.evaluation] ?? 3)) * dir;
              })
              .map((item, i) => (
              <Box key={item.id} sx={{
                bgcolor: i % 2 === 0
                  ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
                  : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C800"),
              }}>
                <Box sx={{ px: 2.5, py: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: evalColor(item.evaluation, theme) }} />
                    <Typography variant="body1" fontWeight={500}>{item.genus}</Typography>
                    <Chip
                      label={getTaxonomyRank(item.taxonomy) || "—"}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.6rem", height: 20 }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", pl: "16px", mt: 0.75 }}>
                    <Typography variant="body1" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {Number(item.frequency).toFixed(2)} %
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {item.evaluation === "N"
                      ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                      : <Typography variant="subtitle2" fontWeight={700} sx={{ color: evalColor(item.evaluation, theme) }}>
                          {evalLabel(item.evaluation, t)}
                        </Typography>
                    }
                  </Box>
                  <Typography variant="caption" color="text.disabled" sx={{ display: "block", pl: "16px", mt: 0.5 }}>
                    Ref: {item.referenceMin} – {item.referenceMax}
                  </Typography>
                </Box>
              </Box>
            ))}
          </AccordionDetails>
          </Accordion>
        </Box>
      </Grid>
    </Grid>
  );
}
